// Bottom navigation editor: 3-5 items, each with a destination, label per
// language, icon, optional FAB style. Reorder via drag-drop using @dnd-kit.

import { useTranslation } from 'react-i18next';
import { Input, Label, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Button } from '@erp/ui';
import { Trash2, Plus, GripVertical } from '@/components/icons';
import type { BottomNavItem, ModulesFlags } from '@erp/app-builder';
import { navigableScreens } from '@erp/app-builder';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface BottomNavTabProps {
  value: BottomNavItem[];
  onChange: (next: BottomNavItem[]) => void;
  enabledModules: ModulesFlags;
}

const ICON_OPTIONS = [
  'home', 'receipt', 'shopping-cart', 'users', 'menu', 'package', 'truck',
  'warehouse', 'landmark', 'banknote', 'book-check', 'settings', 'bell',
  'file-text', 'clipboard-list', 'globe', 'monitor', 'briefcase', 'plus',
  'scan-barcode', 'search', 'sliders-horizontal', 'palette',
];

export function BottomNavTab({ value, onChange, enabledModules }: BottomNavTabProps) {
  const { t } = useTranslation();

  // PointerSensor with a small distance constraint so a click on a button
  // inside a card doesn't accidentally start a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function update(idx: number, patch: Partial<BottomNavItem>) {
    onChange(value.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function remove(idx: number) {
    if (value.length <= 3) return;
    onChange(value.filter((_, i) => i !== idx));
  }

  function add() {
    if (value.length >= 5) return;
    onChange([
      ...value,
      {
        id: `nav-${Date.now()}`,
        icon: 'menu',
        label: { fr: 'Nouveau', en: 'New', ar: 'جديد' },
        destination: 'settings.hub',
        isFab: false,
        quickActions: [],
      },
    ]);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = value.findIndex((it) => it.id === active.id);
    const newIdx = value.findIndex((it) => it.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onChange(arrayMove(value, oldIdx, newIdx));
  }

  const availableScreens = navigableScreens().filter((s) => {
    if (s.module === 'core' || s.module === 'settings') return true;
    return (enabledModules as Record<string, boolean>)[s.module] === true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('appBuilder.bottomNav.help', {
            defaultValue: 'Glissez pour réorganiser. Choisissez 3 à 5 destinations.',
          })}
        </p>
        <Button size="sm" variant="outline" onClick={add} disabled={value.length >= 5}>
          <Plus size={16} />
          {t('appBuilder.bottomNav.add', { defaultValue: 'Ajouter' })}
        </Button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={value.map((v) => v.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {value.map((item, idx) => (
              <SortableNavItem
                key={item.id}
                item={item}
                idx={idx}
                canRemove={value.length > 3}
                onRemove={() => remove(idx)}
                onUpdate={(patch) => update(idx, patch)}
                onToggleFab={(wantFab) => {
                  onChange(
                    value.map((it, i) =>
                      i === idx ? { ...it, isFab: wantFab } : wantFab && it.isFab ? { ...it, isFab: false } : it,
                    ),
                  );
                }}
                availableScreens={availableScreens}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

interface SortableNavItemProps {
  item: BottomNavItem;
  idx: number;
  canRemove: boolean;
  onRemove: () => void;
  onUpdate: (patch: Partial<BottomNavItem>) => void;
  onToggleFab: (wantFab: boolean) => void;
  availableScreens: ReturnType<typeof navigableScreens>;
}

function SortableNavItem({ item, idx, canRemove, onRemove, onUpdate, onToggleFab, availableScreens }: SortableNavItemProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="rounded-lg border bg-card p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab touch-none p-1 -ml-1 rounded hover:bg-muted"
            aria-label="Drag to reorder"
          >
            <GripVertical size={16} />
          </button>
          <span className="text-sm font-semibold">#{idx + 1}</span>
          {item.isFab ? (
            <span className="text-[10px] px-2 py-0.5 rounded bg-primary text-primary-foreground">FAB</span>
          ) : null}
        </div>
        <Button
          size="icon"
          variant="ghost"
          onClick={onRemove}
          disabled={!canRemove}
          className="text-destructive"
        >
          <Trash2 size={16} />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">{t('appBuilder.bottomNav.icon', { defaultValue: 'Icône' })}</Label>
          <Select value={item.icon} onValueChange={(v) => onUpdate({ icon: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {ICON_OPTIONS.map((name) => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t('appBuilder.bottomNav.destination', { defaultValue: 'Destination' })}</Label>
          <Select value={item.destination} onValueChange={(v) => onUpdate({ destination: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {availableScreens.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">FR</Label>
          <Input value={item.label.fr} onChange={(e) => onUpdate({ label: { ...item.label, fr: e.target.value } })} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">AR</Label>
          <Input dir="rtl" value={item.label.ar ?? ''} onChange={(e) => onUpdate({ label: { ...item.label, ar: e.target.value } })} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">EN</Label>
          <Input value={item.label.en ?? ''} onChange={(e) => onUpdate({ label: { ...item.label, en: e.target.value } })} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={item.isFab}
          onChange={(e) => onToggleFab(e.target.checked)}
        />
        {t('appBuilder.bottomNav.fab', { defaultValue: 'Bouton centré (style POS)' })}
      </label>
    </div>
  );
}
